import { UserService, AuthenticationError } from '@/services/user.service'
import { TokenService } from '@/services/storage.service'
import ApiService from '@/services/api.service'
import router from '@/router'
import { canSeePulseChat, canSeePulseTabs, canSeePulseWiki } from '@/services/pulseAccess'

const state =  {
    authenticating: false,
    accessToken: TokenService.getToken(),
    userData: TokenService.getUserData(),
    authenticationErrorCode: 0,
    authenticationError: '',
    mfaEnabled: false
}

const getters = {
    loggedIn: (state) => {
        return state.accessToken ? true : false
    },

    authenticationErrorCode: (state) => {
        return state.authenticationErrorCode
    },

    authenticationError: (state) => {
        return state.authenticationError
    },

    authenticating: (state) => {
        return state.authenticating
    },
    userData (state) {
        // console.log('user %o', state.userData)
        // return JSON.parse(state.userData)
        return state.userData
    },
    userHasSysAdminRole (state) {
        return state.userData && state.userData.roles && state.userData.roles.includes('systemadmin')
    },
    userHasSalesAdminRole (state) {
        return state.userData && state.userData.roles && state.userData.roles.includes('salesadmin')
    },
    userHasPublicRole (state) {
        return state.userData && state.userData.roles 
            && (state.userData.roles.includes('salesrep')
                || state.userData.roles.includes('salessupport'))
    },
    userHasInternalRole (state) {
        // console.log(state.userData)

        return state.userData && 
            state.userData.roles && 
            (state.userData.roles.includes('csrep') 
                || state.userData.roles.includes('insiderep')
                || state.userData.roles.includes('systemadmin')
            )
    },
    userHasSalesRepRole (state) {
        return state.userData && state.userData.roles && state.userData.roles.includes('salesrep')
    },
    userHasCSRole (state) {
        return state.userData && state.userData.roles && state.userData.roles.includes('csrep')
    },
    userHasInsideRepRole (state) {
        return state.userData && state.userData.roles && state.userData.roles.includes('insiderep')
    },
    userHasSalesQuoteManagerRole (state) {
        return state.userData && state.userData.roles && state.userData.roles.includes('salesQuoteManager')
    },
    userHasSalesSupportRole (state) {
        return state.userData && state.userData.roles && state.userData.roles.includes('salessupport')
    },
    userHasContractdeptRole (state) {
        return state.userData && state.userData.roles && state.userData.roles.includes('contractdept')
    },
    // Pulse tab access. These delegate rather than testing roles inline, because the router
    // guard has to reach the same verdict and cannot import this store: auth.module.js
    // imports the router, so the reverse import would close a cycle.
    userCanSeePulseWiki (state) {
        return canSeePulseWiki(state.userData)
    },
    userCanSeePulseChat (state) {
        return canSeePulseChat(state.userData)
    },
    userCanSeePulseTabs (state) {
        return canSeePulseTabs(state.userData)
    },
    // 20201123, if it is local dev or internal HM network, = true
    // if it is public url site, = false
    isHMInternalSite() {
        return process.env.VUE_APP_ROOT_API == '/'
    },
    // Whether any MFA method (email, TOTP or SMS) is enabled on this deployment.
    mfaEnabled (state) {
        return state.mfaEnabled
    },
}

const actions = {
    async login({ commit, dispatch }, {email, password}) {
        commit('loginRequest');

        try {
            commit('setLoading', true, { root: true })
            commit('clearError', null, { root: true })
            const result = await UserService.login(email, password);

            if (result && result.mfaRequired) {
                await dispatch('mfa/setChallenge', {
                    mfaToken: result.mfaToken,
                    method: result.mfaMethod,
                    maskedEmail: result.maskedEmail,
                    expiresIn: result.expiresIn
                }, { root: true })
                return { mfaRequired: true, method: result.mfaMethod }
            }

            commit('loginSuccess', result.accessToken)
            return { mfaRequired: false }
        } catch (e) {
            if (e instanceof AuthenticationError) {
                commit('loginError', {errorCode: e.errorCode, errorMessage: e.message})
            }

            return { mfaRequired: false, error: true }
        } finally {
            commit('setLoading', false, { root: true })

        }
    },

    logout({ commit }) {
        UserService.logout()
        commit('logoutSuccess')
        router.push('/login')
    },

    // Loads deployment MFA config so the UI can hide MFA-only features such as the
    // "My Account" menu when no MFA method is enabled in Web.config.
    async loadMfaConfig({ commit }) {
        try {
            const r = await ApiService.get('/api/Account/UserInfo')
            commit('setMfaEnabled', !!(r.data && r.data.MfaEnabled))
        } catch (e) {
            commit('setMfaEnabled', false)
        }
    }
}

const mutations = {
    loginRequest(state) {
        state.authenticating = true;
        state.authenticationError = ''
        state.authenticationErrorCode = 0
    },

    loginSuccess(state, accessToken) {
        state.accessToken = accessToken
        state.authenticating = false;
        state.userData = TokenService.getUserData()
    },

    loginError(state, {errorCode, errorMessage}) {
        state.authenticating = false
        state.authenticationErrorCode = errorCode
        state.authenticationError = errorMessage
    },

    logoutSuccess(state) {
        state.accessToken = ''
        state.userData = ''
        state.mfaEnabled = false
    },

    setMfaEnabled(state, value) {
        state.mfaEnabled = value
    }
}

export const auth = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}